import { nextTick, onUnmounted, ref, watch } from "vue"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

const isEscapePress = (event) => {
  return (
    event?.key === "Escape" ||
    event?.key === "Esc" ||
    event?.code === "Escape" ||
    event?.keyCode === 27
  )
}

const hasVisibleModalState = (element) => {
  if (!element) return false
  if (element.classList?.contains("show")) return true
  if (element.getAttribute("aria-modal") === "true") return true
  const style = element.getAttribute("style") || ""
  return style.includes("display: block")
}

const isVisibleElement = (element) => {
  if (!element) return false
  return element.offsetWidth > 0 || element.offsetHeight > 0
}

const getVisibleModals = () => {
  return Array.from(document.querySelectorAll(".modal")).filter((element) => {
    return hasVisibleModalState(element) && isVisibleElement(element)
  })
}

const canRestoreFocus = (target) => {
  if (!target || typeof target.focus !== "function") return false
  if (target.isConnected === false) return false
  if ("ownerDocument" in target && target.ownerDocument) {
    return target.ownerDocument.contains(target)
  }
  return true
}

const restoreFocus = (target) => {
  if (!canRestoreFocus(target)) return
  try {
    target.focus()
  } catch {
    // no-op: focus can fail in transient DOM states
  }
}

const getFocusableElements = (modalElement) => {
  if (!modalElement) return []
  return Array.from(modalElement.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => {
      return (
        !element.hasAttribute("disabled") &&
        element.getAttribute("aria-hidden") !== "true" &&
        (element.offsetWidth > 0 || element.offsetHeight > 0)
      )
    }
  )
}

export default function useLegacyModalFocusTrap(
  modalId,
  isVisible,
  closeModal,
  getModalElement
) {
  const lastFocusedElement = ref(null)

  const getCurrentModalElement = () => {
    const resolvedModalElement = getModalElement?.()
    if (
      resolvedModalElement &&
      resolvedModalElement.isConnected &&
      isVisibleElement(resolvedModalElement)
    ) {
      return resolvedModalElement
    }

    const root = document.getElementById(modalId)
    if (root) {
      if (root.classList?.contains("modal")) return root
      const nestedModal = root.querySelector?.(".modal")
      if (nestedModal) return nestedModal
      const closestModal = root.closest?.(".modal")
      if (closestModal) return closestModal
    }

    const visibleModals = getVisibleModals()
    if (!visibleModals.length) return null
    return visibleModals[visibleModals.length - 1]
  }

  const isCurrentModalTopMost = () => {
    const currentModal = getCurrentModalElement()
    if (!currentModal) return false
    const visibleModals = getVisibleModals()
    const topMostModal = visibleModals[visibleModals.length - 1]
    return currentModal === topMostModal
  }

  const focusFirstElement = () => {
    const modalElement = getCurrentModalElement()
    if (!modalElement) return
    const focusableElements = getFocusableElements(modalElement)
    if (focusableElements.length) {
      focusableElements[0].focus()
      return
    }
    modalElement.setAttribute("tabindex", "-1")
    modalElement.focus()
  }

  const onDocumentKeydown = (event) => {
    if (!isVisible.value || !isCurrentModalTopMost()) return
    const modalElement = getCurrentModalElement()
    if (!modalElement) return

    if (isEscapePress(event)) {
      event.preventDefault()
      event.stopPropagation()
      closeModal()
      return
    }

    if (event.key !== "Tab") return

    const focusableElements = getFocusableElements(modalElement)
    if (!focusableElements.length) {
      event.preventDefault()
      modalElement.focus()
      return
    }

    const activeElement = document.activeElement
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (!modalElement.contains(activeElement)) {
      event.preventDefault()
      ;(event.shiftKey ? lastElement : firstElement).focus()
      return
    }

    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  const onDocumentFocusIn = (event) => {
    if (!isVisible.value || !isCurrentModalTopMost()) return
    const modalElement = getCurrentModalElement()
    if (!modalElement) return
    if (event.target && !modalElement.contains(event.target)) {
      const focusableElements = getFocusableElements(modalElement)
      const target = focusableElements[0] || modalElement
      target?.focus?.()
    }
  }

  watch(
    isVisible,
    (visible) => {
      if (visible) {
        lastFocusedElement.value = document.activeElement
        nextTick(() => {
          window.setTimeout(() => {
            focusFirstElement()
          }, 0)
        })
        return
      }

      const returnTo = lastFocusedElement.value
      nextTick(() => {
        restoreFocus(returnTo)
        window.setTimeout(() => restoreFocus(returnTo), 50)
      })
      lastFocusedElement.value = null
    },
    { immediate: true }
  )

  document.addEventListener("keydown", onDocumentKeydown, true)
  document.addEventListener("focusin", onDocumentFocusIn, true)

  onUnmounted(() => {
    document.removeEventListener("keydown", onDocumentKeydown, true)
    document.removeEventListener("focusin", onDocumentFocusIn, true)
  })
}
